import MoneyInput from '~/components/MoneyInput';
import OptionToggle from '~/components/OptionToggle';
import TextInput from '~/components/TextInput';
import TextArea from '~/components/TextArea';
import TextInputWithInlineLabel from '~/components/TextInputWithInlineLabel';
import SidePanel from '~/components/SidePanel';
import SectionPicker from '~/components/SectionPicker.jsx';
import SectionCategoryLabel from '~/components/SectionCategoryLabel.jsx';
import Toggle from '~/components/Toggle.jsx';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import React, { useMemo, useState } from 'react';
import { DEFAULT_GIFT_CERTIFICATE_VALUES } from '~/components/forms/questions/GiftCertificateQuestion';
import { useEventTypesQuery, usePackagesQuery } from '~/lib/Queries';
import MagnifyingGlassIcon from '~/components/icons/MagnifyingGlassIcon.jsx';
import EditBackgroundImagesPanel from './EditBackgroundImagesPanel.jsx';

const GiftCertListItem = ({ value, onClick }) => {
    const isObj = typeof value === 'object';
    const hasName = isObj && !!value?.name?.trim();
    const amount = isObj ? value?.value : value;
    const description = isObj ? value?.description : '';
    return (
        <div
            type="button"
            onClick={onClick}
            className="cursor-pointer group py-3 pr-4 flex justify-between items-center"
        >
            <div>
                <div>{hasName ? value.name : (amount ? formatCurrency(amount) : 'Unnamed Gift Certificate')}</div>
                <div className="text-gray-500 text-sm">
                    {amount > 0 && <span>{formatCurrency(amount)}</span>}
                </div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gray-500" />
        </div>
    );
};

const EditGiftCertificatePanel = ({ value, setValue, isOpen, onClose, onDelete }) => {
    const [error, setError] = useState('');

    return (
        <SidePanel title="Gift Certificates" isOpen={isOpen} onClose={onClose}>
            <TextInput
                label="Name"
                placeholder="Type name..."
                value={value?.name || ''}
                onChange={newValue => setValue({ ...value, name: newValue })}
            />

            <TextArea
                className="mt-4"
                label="Description"
                placeholder="Type description..."
                value={value?.description || ''}
                onChange={newValue => setValue({ ...value, description: newValue })}
            />

            <MoneyInput
                className="mt-4"
                label="Amount"
                value={value?.value || ''}
                onChange={newValue => {
                    setValue({ ...value, value: newValue });
                    if (newValue && parseFloat(newValue) > 0) setError('');
                }}
                required
            />
            {!!error && (
                <div className="text-red-600 text-sm mt-1">{error}</div>
            )}

            <div className="mt-4 flex items-center justify-between">
                <button
                    className="btn-save"
                    type="button"
                    onClick={() => {
                        const amt = parseFloat(value?.value);
                        if (!value?.value || isNaN(amt) || amt <= 0) {
                            setError('Amount is required');
                            return;
                        }
                        onClose();
                    }}
                >
                    Save
                </button>
                <button
                    className="text-red-700"
                    type="button"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                >
                    Delete
                </button>
            </div>
        </SidePanel>
    );
};

const EditGiftCertificateForm = ({ settings, setSettings }) => {
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [section, setSection] = useState('events');
    const [search, setSearch] = useState('');
    const [isBackgroundImagesOpen, setIsBackgroundImagesOpen] = useState(false);

    const rawValues = Array.isArray(settings?.possibleValues)
        ? settings.possibleValues
        : DEFAULT_GIFT_CERTIFICATE_VALUES;
    const values = rawValues.map(v => (typeof v === 'object' ? v : { name: '', description: '', value: v }));

    // Linked selections (events/packages/subscriptions)
    const linked = settings?.giftCertLinked || {};
    const enabledEventTypeIds = linked?.event_type_ids || [];
    const enabledPackageIds = linked?.package_ids || [];
    const enabledSubscriptionIds = linked?.subscription_ids || [];

    const { data: eventTypesData } = useEventTypesQuery();
    const allEventTypes = eventTypesData || [];

    const { data: packageData } = usePackagesQuery('package');
    const { data: subscriptionData } = usePackagesQuery('subscription');
    const allPackages = packageData?.packages || [];
    const allSubscriptions = subscriptionData?.packages || [];

    function toggleLinked(type, id, isEnabled) {
        const current = settings?.giftCertLinked || {};
        const arrName = type === 'event_type' ? 'event_type_ids' : (type === 'package' ? 'package_ids' : 'subscription_ids');
        const existing = current[arrName] || [];
        let next = existing;
        if (isEnabled) {
            next = [...existing, id];
        } else {
            next = existing.filter(x => x !== id);
        }
        setSettings({
            ...settings,
            giftCertLinked: {
                ...current,
                [arrName]: next,
            },
        });
    }

    const linkedKeys = useMemo(() => {
        const set = new Set();
        enabledEventTypeIds.forEach(id => {
            const et = (allEventTypes || []).find(e => parseInt(e.id) === parseInt(id));
            if (et && et.name) set.add(`${et.name}||${et.price}`);
        });
        enabledPackageIds.forEach(id => {
            const p = (allPackages || []).find(e => parseInt(e.id) === parseInt(id));
            if (p && p.name) set.add(`${p.name}||${p.price}`);
        });
        enabledSubscriptionIds.forEach(id => {
            const s = (allSubscriptions || []).find(e => parseInt(e.id) === parseInt(id));
            if (s && s.name) set.add(`${s.name}||${s.price}`);
        });
        return set;
    }, [enabledEventTypeIds, enabledPackageIds, enabledSubscriptionIds, allEventTypes, allPackages, allSubscriptions]);

    const displayPairs = values.map((v, i) => ({ v, i }))
        .filter(({ v }) => !(v?.name && linkedKeys.has(`${v.name}||${v.value}`)));

    const lowerSearch = (search || '').toLowerCase();

    const filteredEvents = (allEventTypes || [])
        .filter(et => parseFloat(et?.price) > 0)
        .filter(et => !lowerSearch || (et?.name || '').toLowerCase().includes(lowerSearch));

    const categoryMeta = {};
    const noCategoryName = '';
    const eventsByCategory = filteredEvents.reduce((acc, et) => {
        const catName = et?.category?.name || noCategoryName;
        const catOrder = et?.category?.order;
        if (!categoryMeta[catName]) {
            categoryMeta[catName] = { order: Number.isFinite(+catOrder) ? +catOrder : null };
        }
        (acc[catName] ||= []).push(et);
        return acc;
    }, {});
    // Ensure Other is first
    categoryMeta[noCategoryName] = categoryMeta[noCategoryName] || { order: Number.NEGATIVE_INFINITY };
    const eventCategories = Object.keys(eventsByCategory).sort((a, b) => {
        if (a === noCategoryName && b !== noCategoryName) return -1;
        if (b === noCategoryName && a !== noCategoryName) return 1;
        const oa = Number.isFinite(categoryMeta[a]?.order) ? categoryMeta[a].order : Number.MAX_SAFE_INTEGER;
        const ob = Number.isFinite(categoryMeta[b]?.order) ? categoryMeta[b].order : Number.MAX_SAFE_INTEGER;
        if (oa !== ob) return oa - ob;
        return a.localeCompare(b);
    });
    const filteredPackages = (allPackages || []).filter(p =>
        !lowerSearch || (p?.name || '').toLowerCase().includes(lowerSearch),
    );
    const filteredSubscriptions = (allSubscriptions || []).filter(p =>
        !lowerSearch || (p?.name || '').toLowerCase().includes(lowerSearch),
    );

    return (
        <div className="mt-6">
            <TextInputWithInlineLabel
                value={settings?.expires || ''}
                onChange={value => setSettings({ ...settings, expires: value })}
                label="Certificate Expires After"
                placeholder="No expiration"
                suffix="years"
                className="max-w-52"
            />

            <div className="panel-label mt-6">Images</div>
            <div className="panel">
                <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3"
                    onClick={() => setIsBackgroundImagesOpen(true)}
                >
                    <div className="text-left">
                        <div>Set Background Images</div>
                        <div className="text-gray-500 text-sm">
                            {settings?.customBackgroundImages?.length > 0
                                ? `${settings?.customBackgroundImages?.length} Custom image${settings?.customBackgroundImages?.length>1 ? 's' : ''} set`
                                : 'Currently using default images'}
                        </div>

                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </button>
            </div>

            <div className="panel-label mt-6">Gift Certificate Values</div>
            <div className="panel pl-4 divide-y mt-2">
                {(displayPairs || []).map(({ v, i }) => (
                    <GiftCertListItem key={i} value={v} onClick={() => setSelectedIdx(i)} />
                ))}
                <OptionToggle
                    title="Allow clients to choose a custom value"
                    value={!settings?.disableCustom}
                    onChange={() =>
                        setSettings({
                            ...settings,
                            disableCustom: !settings?.disableCustom,
                        })
                    }
                    toggleFirst={false}
                    className="py-4 pr-4"
                />
            </div>

            <button
                type="button"
                className="btn-light-pill mt-2"
                onClick={() => {
                    const newValues = [...values, { name: '', description: '', value: '' }];
                    setSettings({ ...settings, possibleValues: newValues });
                    setSelectedIdx(newValues.length - 1);
                }}
            >
                Add New
            </button>

            {/* Linked items section */}
            <div className="mt-8">
                {/*<div className="panel-label">Use price from</div>*/}
                <SectionPicker
                    value={section}
                    onChange={setSection}
                    options={[
                        { label: 'Events', value: 'events' },
                        { label: 'Packages', value: 'packages' },
                    ]}
                    className="inline-flex"
                    slim
                />

                <div className="mt-2">
                    <TextInputWithInlineLabel
                        value={search}
                        onChange={setSearch}
                        placeholder={`Filter ${section === 'events' ? 'events' : 'packages'}...`}
                        // label="Filter"
                        inputClassName="pl-10"
                        prefix={<MagnifyingGlassIcon className="w-4 h-4 text-primary-700" />}
                    />
                </div>

                {section === 'events' && (
                    <div className="mt-2 space-y-4">
                        {eventCategories.map(cat => (
                            <div key={cat}>
                                <SectionCategoryLabel>{cat}</SectionCategoryLabel>
                                <div className="panel pl-4 divide-y mt-2">
                                    {(eventsByCategory[cat] || []).map(et => (
                                        <div key={et.id} className="py-3 pr-4 flex items-center justify-between">
                                            <div>
                                                <div>{et.name}</div>
                                                <div className="text-gray-500 text-sm">{formatCurrency(et.price)}</div>
                                            </div>
                                            <Toggle
                                                enabled={enabledEventTypeIds.includes(et.id)}
                                                setEnabled={val => toggleLinked('event_type', et.id, val)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {section === 'packages' && (
                    <div className="panel pl-4 divide-y mt-2">
                        {(filteredPackages || []).map(p => (
                            <div key={`pkg-${p.id}`} className="py-3 pr-4 flex items-center justify-between">
                                <div>
                                    <div>{p.name}</div>
                                    <div className="text-gray-500 text-sm">{formatCurrency(p.price)}</div>
                                </div>
                                <Toggle
                                    enabled={enabledPackageIds.includes(p.id)}
                                    setEnabled={val => toggleLinked('package', p.id, val)}
                                />
                            </div>
                        ))}
                        {(filteredSubscriptions || []).map(s => (
                            <div key={`sub-${s.id}`} className="py-3 pr-4 flex items-center justify-between">
                                <div>
                                    <div>{s.name} <span className="text-gray-400 text-xs align-middle">(Subscription)</span></div>
                                    <div className="text-gray-500 text-sm">{formatCurrency(s.price)}</div>
                                </div>
                                <Toggle
                                    enabled={enabledSubscriptionIds.includes(s.id)}
                                    setEnabled={val => toggleLinked('subscription', s.id, val)}
                                />
                            </div>
                        ))}
                        {!filteredSubscriptions?.length && !filteredPackages?.length && (
                            <div className="text-gray-500 py-4">No packages or subscriptions found</div>
                        )}
                    </div>
                )}
            </div>

            <EditGiftCertificatePanel
                value={selectedIdx !== null ? values[selectedIdx] : {}}
                setValue={newValue => {
                    const newValues = [...values];
                    newValues[selectedIdx] = newValue;
                    setSettings({ ...settings, possibleValues: newValues });
                }}
                onDelete={() => {
                    const newValues = [...values];
                    newValues.splice(selectedIdx, 1);
                    setSettings({ ...settings, possibleValues: newValues });
                }}
                isOpen={selectedIdx !== null}
                onClose={() => setSelectedIdx(null)}
            />

            <EditBackgroundImagesPanel
                isOpen={isBackgroundImagesOpen}
                onClose={() => setIsBackgroundImagesOpen(false)}
                customImages={settings?.customBackgroundImages || []}
                setCustomImages={images => setSettings({ ...settings, customBackgroundImages: images })}
            />
        </div>
    );
};

export default EditGiftCertificateForm;
